import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';
import {
    BENEFIT_FILTERS, EMPTY_DISPLAY_CONFIG, displayConfigFromFlag, validateDisplayConfig,
    displayConfigWarning, getNotifySignupCounts, isLiveOnBenefits, sendAllLaunchEmailBatches,
} from '../utils/programFlagDisplay';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

function ProgramFlagsAdd() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(id ? true : false);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        key: '',
        name: '',
        description: '',
        category: 'program',
        status: 'inactive',
        isGloballyEnabled: false,
        phase: 1,
        displayOrder: 0,
        planRestrictions: {
            enabled: false,
            allowedPlans: []
        },
        rollout: {
            enabled: false,
            percentage: 100,
            strategy: 'percentage'
        },
        schedule: {
            startDate: '',
            endDate: ''
        },
        config: {},
        metadata: {
            version: '1.0',
            notes: ''
        }
    });

    const [configJson, setConfigJson] = useState('{}');
    const [configError, setConfigError] = useState('');

    const [displayConfig, setDisplayConfig] = useState(EMPTY_DISPLAY_CONFIG);

    // "Notify me" sign-ups. The Send button follows the SAVED flag, not
    // unsaved form edits — the API only sends once the saved card is live.
    const [savedFlag, setSavedFlag] = useState(null);
    const [notifyCounts, setNotifyCounts] = useState(null);
    // null when idle, else { sent, total } while batches are going out.
    const [sendProgress, setSendProgress] = useState(null);

    const fetchNotifyCounts = useCallback(async () => {
        try {
            setNotifyCounts(await getNotifySignupCounts(id));
        } catch (error) {
            console.error('Error fetching notify sign-ups:', error);
        }
    }, [id]);

    const handleSendLaunchEmail = async () => {
        const name = displayConfig.displayName || formData.name;
        const total = notifyCounts.waiting;
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Email ${total} people that ${name} is now live? This can't be undone.`)) return;

        setSendProgress({ sent: 0, total });
        let lastRemaining = total;
        try {
            const { sent, failed, remaining } = await sendAllLaunchEmailBatches(id, (progress) => {
                lastRemaining = progress.remaining;
                setSendProgress({ sent: progress.sent, total });
                setNotifyCounts((prev) => ({
                    ...prev,
                    waiting: progress.remaining,
                    notified: (prev?.notified || 0) + progress.batchSent,
                }));
            });
            if (sent > 0) toast.success(`Launch email sent to ${sent} ${sent === 1 ? 'person' : 'people'}`);
            if (remaining > 0) {
                toast.warn(`${remaining} still waiting${failed ? ` (${failed} failed to send)` : ''}. `
                    + 'Failed addresses are retried a few minutes later — click Send again then. '
                    + 'After 3 failed tries an address is counted as undeliverable.');
            } else if (sent === 0) {
                toast.info('Nobody is waiting');
            }
        } catch (error) {
            // e.g. a gateway timeout mid-run — the batch may well have gone out.
            // The API skips anyone already emailed, so retrying is safe.
            toast.error(`Sending was interrupted (${error.message || 'request failed'}). `
                + `About ${lastRemaining} may still be waiting — it's safe to click Send again `
                + '(if nothing moves, try again in a few minutes).');
        } finally {
            setSendProgress(null);
            fetchNotifyCounts();
        }
    };

    const fetchFlagDetails = useCallback(async () => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/program-flags/${id}`
            );
            const data = await res.json();

            if (data.status === 'success') {
                const flag = data.data;
                setSavedFlag(flag);
                setFormData({
                    ...flag,
                    schedule: {
                        startDate: flag.schedule?.startDate ? new Date(flag.schedule.startDate).toISOString().slice(0, 16) : '',
                        endDate: flag.schedule?.endDate ? new Date(flag.schedule.endDate).toISOString().slice(0, 16) : ''
                    }
                });
                const cfg = flag.config || {};
                setDisplayConfig(displayConfigFromFlag(cfg.display));
                // Strip the display namespace from the JSON textarea so
                // technical config (ssoEnabled, launchUrl, etc.) is shown alone.
                const { display: _display, ...technicalConfig } = cfg;
                setConfigJson(JSON.stringify(technicalConfig, null, 2));
            } else {
                toast.error('Failed to fetch flag details');
            }
        } catch (error) {
            console.error('Error fetching flag:', error);
            toast.error('Error fetching flag details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (id) {
            fetchFlagDetails();
            fetchNotifyCounts();
        }
    }, [id, fetchFlagDetails, fetchNotifyCounts]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: type === 'checkbox' ? checked : value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handlePlanChange = (plan) => {
        setFormData(prev => {
            const currentPlans = prev.planRestrictions.allowedPlans || [];
            const newPlans = currentPlans.includes(plan)
                ? currentPlans.filter(p => p !== plan)
                : [...currentPlans, plan];

            return {
                ...prev,
                planRestrictions: {
                    ...prev.planRestrictions,
                    allowedPlans: newPlans
                }
            };
        });
    };

    const handleDisplayConfigChange = (e) => {
        const { name, value, type, checked } = e.target;
        setDisplayConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
            // "Notify me" only exists on a coming-soon card — don't keep a
            // hidden value that silently comes back if Coming Soon is re-enabled.
            ...(name === 'comingSoon' && !checked ? { notifyMe: false } : {}),
        }));
    };

    const handleFilterCategoryChange = (category) => {
        setDisplayConfig(prev => ({
            ...prev,
            filterCategories: prev.filterCategories.includes(category)
                ? prev.filterCategories.filter(c => c !== category)
                : [...prev.filterCategories, category]
        }));
    };

    const handleConfigChange = (e) => {
        const value = e.target.value;
        setConfigJson(value);

        try {
            JSON.parse(value);
            setConfigError('');
        } catch (err) {
            setConfigError('Invalid JSON format');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate config JSON
        let parsedConfig = {};
        try {
            parsedConfig = JSON.parse(configJson);
        } catch (err) {
            toast.error('Invalid JSON in config field');
            return;
        }

        // Validate required fields
        if (!formData.key || !formData.name) {
            toast.error('Key and Name are required');
            return;
        }

        // Validate display fields when card is shown on member page
        const displayError = validateDisplayConfig(displayConfig);
        if (displayError) {
            toast.error(displayError);
            return;
        }

        try {
            setSaving(true);

            const payload = {
                ...formData,
                // Namespace display fields under config.display to prevent
                // collision with technical config keys (e.g. path, icon).
                config: { ...parsedConfig, display: displayConfig },
                phase: parseInt(formData.phase),
                displayOrder: parseInt(formData.displayOrder),
                rollout: {
                    ...formData.rollout,
                    percentage: parseInt(formData.rollout.percentage)
                },
                schedule: {
                    startDate: formData.schedule.startDate ? new Date(formData.schedule.startDate).toISOString() : null,
                    endDate: formData.schedule.endDate ? new Date(formData.schedule.endDate).toISOString() : null
                }
            };

            const url = id
                ? `${Base_Url}/admin/program-flags/${id}`
                : `${Base_Url}/admin/program-flags`;

            const method = id ? 'PUT' : 'POST';

            const res = await authManager.authenticatedFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (data.status === 'success') {
                const displayWarning = displayConfigWarning(displayConfig);
                if (displayWarning) toast.warn(displayWarning, { autoClose: 8000 });
                // Just published a card people are waiting on — stay here so
                // the launch email isn't forgotten.
                if (id && isLiveOnBenefits(data.data) && notifyCounts?.waiting > 0) {
                    setSavedFlag(data.data);
                    toast.success(`Flag updated. ${notifyCounts.waiting} people are waiting — click "Send launch email" below.`, { autoClose: 8000 });
                    return;
                }
                toast.success(id ? 'Flag updated successfully!' : 'Flag created successfully!');
                setTimeout(() => navigate('/programFlags'), 1500);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (err) {
            console.error('Error saving flag:', err);
            toast.error('Error saving flag');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">{id ? 'Edit' : 'Add'} Program Flag</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/programFlags">
                                        <i className="fa-solid fa-arrow-left-long" /> Back
                                    </Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    {loading ? (
                                        <div className="card-body text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <form className="form-horizontal" onSubmit={handleSubmit}>
                                            <div className="card-body">
                                                {/* Basic Information */}
                                                <h5 className="f_blue pb-3 mb-3 border-bottom">Basic Information</h5>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Key *</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="key"
                                                            value={formData.key}
                                                            onChange={handleInputChange}
                                                            disabled={!!id}
                                                            placeholder="e.g., forum, shopping, goaltide"
                                                        />
                                                        {id && (
                                                            <small className="text-muted">Key cannot be changed after creation</small>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Name *</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="name"
                                                            value={formData.name}
                                                            onChange={handleInputChange}
                                                            placeholder="Display name for this feature"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Description</label>
                                                    <div className="col-12 col-md-9">
                                                        <textarea
                                                            className="form-control"
                                                            name="description"
                                                            value={formData.description}
                                                            onChange={handleInputChange}
                                                            rows="3"
                                                            placeholder="Brief description of what this flag controls"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Category</label>
                                                    <div className="col-12 col-md-9">
                                                        <select
                                                            className="form-control"
                                                            name="category"
                                                            value={formData.category}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value="program">Program</option>
                                                            <option value="feature">Feature</option>
                                                            <option value="integration">Integration</option>
                                                            <option value="ui">UI</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Phase</label>
                                                    <div className="col-12 col-md-9">
                                                        <select
                                                            className="form-control"
                                                            name="phase"
                                                            value={formData.phase}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value="1">Phase 1 (Free Community)</option>
                                                            <option value="2">Phase 2 (Premium)</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Display Order</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            name="displayOrder"
                                                            value={formData.displayOrder}
                                                            onChange={handleInputChange}
                                                            min="0"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Status & Visibility */}
                                                <h5 className="f_blue pb-3 mb-3 mt-4 border-bottom">Status & Visibility</h5>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Status</label>
                                                    <div className="col-12 col-md-9">
                                                        <select
                                                            className="form-control"
                                                            name="status"
                                                            value={formData.status}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                            <option value="deprecated">Deprecated</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Globally Enabled</label>
                                                    <div className="col-12 col-md-9">
                                                        <div className="custom-control custom-switch mt-2">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="isGloballyEnabled"
                                                                name="isGloballyEnabled"
                                                                checked={formData.isGloballyEnabled}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="isGloballyEnabled">
                                                                {formData.isGloballyEnabled ? 'Enabled' : 'Disabled'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Plan Restrictions */}
                                                <h5 className="f_blue pb-3 mb-3 mt-4 border-bottom">Plan Restrictions</h5>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Enable Plan Gating</label>
                                                    <div className="col-12 col-md-9">
                                                        <div className="custom-control custom-switch mt-2">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="planRestrictionsEnabled"
                                                                name="planRestrictions.enabled"
                                                                checked={formData.planRestrictions.enabled}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="planRestrictionsEnabled">
                                                                {formData.planRestrictions.enabled ? 'Enabled' : 'Disabled'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                {formData.planRestrictions.enabled && (
                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-3 col-form-label">Allowed Plans</label>
                                                        <div className="col-12 col-md-9">
                                                            {['free', 'founder', 'premium', 'enterprise'].map(plan => (
                                                                <div className="custom-control custom-checkbox custom-control-inline" key={plan}>
                                                                    <input
                                                                        type="checkbox"
                                                                        className="custom-control-input"
                                                                        id={`plan-${plan}`}
                                                                        checked={formData.planRestrictions.allowedPlans?.includes(plan)}
                                                                        onChange={() => handlePlanChange(plan)}
                                                                    />
                                                                    <label className="custom-control-label" htmlFor={`plan-${plan}`}>
                                                                        {plan.charAt(0).toUpperCase() + plan.slice(1)}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Rollout Configuration */}
                                                <h5 className="f_blue pb-3 mb-3 mt-4 border-bottom">Rollout Configuration</h5>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Enable Rollout</label>
                                                    <div className="col-12 col-md-9">
                                                        <div className="custom-control custom-switch mt-2">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="rolloutEnabled"
                                                                name="rollout.enabled"
                                                                checked={formData.rollout.enabled}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="rolloutEnabled">
                                                                {formData.rollout.enabled ? 'Enabled' : 'Disabled'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                {formData.rollout.enabled && (
                                                    <>
                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-3 col-form-label">Strategy</label>
                                                            <div className="col-12 col-md-9">
                                                                <select
                                                                    className="form-control"
                                                                    name="rollout.strategy"
                                                                    value={formData.rollout.strategy}
                                                                    onChange={handleInputChange}
                                                                >
                                                                    <option value="percentage">Percentage</option>
                                                                    <option value="user_list">User List</option>
                                                                </select>
                                                            </div>
                                                        </div>

                                                        {formData.rollout.strategy === 'percentage' && (
                                                            <div className="form-group row">
                                                                <label className="col-12 col-md-3 col-form-label">Percentage</label>
                                                                <div className="col-12 col-md-9">
                                                                    <div className="d-flex align-items-center">
                                                                        <input
                                                                            type="range"
                                                                            className="custom-range mr-3"
                                                                            name="rollout.percentage"
                                                                            value={formData.rollout.percentage}
                                                                            onChange={handleInputChange}
                                                                            min="0"
                                                                            max="100"
                                                                            style={{ flex: 1 }}
                                                                        />
                                                                        <span className="badge badge-primary" style={{ minWidth: '50px' }}>
                                                                            {formData.rollout.percentage}%
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}

                                                {/* Schedule */}
                                                <h5 className="f_blue pb-3 mb-3 mt-4 border-bottom">Schedule (Optional)</h5>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Start Date</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="datetime-local"
                                                            className="form-control"
                                                            name="schedule.startDate"
                                                            value={formData.schedule.startDate}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">End Date</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="datetime-local"
                                                            className="form-control"
                                                            name="schedule.endDate"
                                                            value={formData.schedule.endDate}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Frontend Card Display */}
                                                <h5 className="f_blue pb-3 mb-3 mt-4 border-bottom">Frontend Card Display</h5>
                                                <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                                                    Controls how this program appears on the Member Programs page.
                                                </p>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Show as Program Card</label>
                                                    <div className="col-12 col-md-9">
                                                        <div className="custom-control custom-switch mt-2">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="showAsProgram"
                                                                name="showAsProgram"
                                                                checked={displayConfig.showAsProgram}
                                                                onChange={handleDisplayConfigChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="showAsProgram">
                                                                {displayConfig.showAsProgram ? 'Visible on Member Programs page' : 'Hidden (infrastructure flag)'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                {displayConfig.showAsProgram && (
                                                    <>
                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-3 col-form-label">Display Name <span className="text-danger">*</span></label>
                                                            <div className="col-12 col-md-9">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="displayName"
                                                                    value={displayConfig.displayName}
                                                                    onChange={handleDisplayConfigChange}
                                                                    placeholder="e.g., Goaltide Financial Planning"
                                                                    maxLength={60}
                                                                />
                                                                <small className="text-muted">Card title shown on the benefits page (overrides Name above)</small>
                                                            </div>
                                                        </div>

                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-3 col-form-label">Subtitle</label>
                                                            <div className="col-12 col-md-9">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="subtitle"
                                                                    value={displayConfig.subtitle}
                                                                    onChange={handleDisplayConfigChange}
                                                                    placeholder="e.g., Your Path to Financial Freedom"
                                                                    maxLength={80}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-3 col-form-label">Card Description</label>
                                                            <div className="col-12 col-md-9">
                                                                <textarea
                                                                    className="form-control"
                                                                    name="cardDescription"
                                                                    value={displayConfig.cardDescription}
                                                                    onChange={handleDisplayConfigChange}
                                                                    rows="2"
                                                                    placeholder="Short description shown on the card"
                                                                    maxLength={200}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-3 col-form-label">Badge Text</label>
                                                            <div className="col-12 col-md-9">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="badge"
                                                                    value={displayConfig.badge}
                                                                    onChange={handleDisplayConfigChange}
                                                                    placeholder="e.g., Free Access, 20% Off Tuition, Coming 11/1"
                                                                    maxLength={30}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-3 col-form-label">Coming Soon</label>
                                                            <div className="col-12 col-md-9">
                                                                <div className="custom-control custom-switch mt-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="custom-control-input"
                                                                        id="comingSoon"
                                                                        name="comingSoon"
                                                                        checked={displayConfig.comingSoon}
                                                                        onChange={handleDisplayConfigChange}
                                                                    />
                                                                    <label className="custom-control-label" htmlFor="comingSoon">
                                                                        {displayConfig.comingSoon ? 'Coming soon — no "Access Now" button' : 'Live — shows the "Access Now" button'}
                                                                    </label>
                                                                </div>
                                                                <small className="text-muted">Coming-soon cards show on the Benefits page with an amber badge and no button, and are left out of the dashboard and membership page tiles. The badge uses the Badge Text above (e.g. "Coming 11/1") — with no Badge Text the card shows no badge. Frontend Path is optional.</small>
                                                            </div>
                                                        </div>

                                                        {!displayConfig.comingSoon && (
                                                            <div className="form-group row">
                                                                <label className="col-12 col-md-3 col-form-label">Highlight Badge</label>
                                                                <div className="col-12 col-md-9">
                                                                    <div className="custom-control custom-checkbox mt-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="custom-control-input"
                                                                            id="highlightBadge"
                                                                            name="highlightBadge"
                                                                            checked={displayConfig.highlightBadge}
                                                                            onChange={handleDisplayConfigChange}
                                                                        />
                                                                        <label className="custom-control-label" htmlFor="highlightBadge">
                                                                            Amber badge (e.g., "New")
                                                                        </label>
                                                                    </div>
                                                                    <small className="text-muted">Uses the Badge Text above.</small>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {!displayConfig.comingSoon && (
                                                            <div className="form-group row">
                                                                <label className="col-12 col-md-3 col-form-label">"Access Now" Button</label>
                                                                <div className="col-12 col-md-9">
                                                                    <div className="custom-control custom-switch mt-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="custom-control-input"
                                                                            id="showCta"
                                                                            checked={!displayConfig.hideCta}
                                                                            onChange={(e) => setDisplayConfig(prev => ({ ...prev, hideCta: !e.target.checked }))}
                                                                        />
                                                                        <label className="custom-control-label" htmlFor="showCta">
                                                                            {displayConfig.hideCta ? 'Hidden — card shows its badge and text only' : 'Shown on the card'}
                                                                        </label>
                                                                    </div>
                                                                    <small className="text-muted">Hide it when there's nothing to open yet (e.g. Pharmacy before its page exists). The card keeps its normal badge, is left out of the dashboard and membership page tiles, and Frontend Path becomes optional.</small>
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-3 col-form-label">Icon</label>
                                                            <div className="col-12 col-md-9">
                                                                <div className="input-group">
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        name="icon"
                                                                        value={displayConfig.icon}
                                                                        onChange={handleDisplayConfigChange}
                                                                        placeholder="e.g., fa-solid fa-chart-line"
                                                                    />
                                                                    {displayConfig.icon && (
                                                                        <div className="input-group-append">
                                                                            <span className="input-group-text">
                                                                                <i className={displayConfig.icon} />
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <small className="text-muted">FontAwesome class string</small>
                                                            </div>
                                                        </div>

                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-3 col-form-label">Color Class</label>
                                                            <div className="col-12 col-md-9">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="colorClass"
                                                                    value={displayConfig.colorClass}
                                                                    onChange={handleDisplayConfigChange}
                                                                    placeholder="e.g., goaltide, forum, shopping"
                                                                />
                                                                <small className="text-muted">CSS class suffix for card icon theming (co-benefit-card__icon--&#123;colorClass&#125;) on the dashboard and membership tiles. On the Benefits page every icon is teal except <code>amber</code>.</small>
                                                            </div>
                                                        </div>

                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-3 col-form-label">Benefits Filters</label>
                                                            <div className="col-12 col-md-9">
                                                                {BENEFIT_FILTERS.map(filter => (
                                                                    <div className="custom-control custom-checkbox custom-control-inline" key={filter.value}>
                                                                        <input
                                                                            type="checkbox"
                                                                            className="custom-control-input"
                                                                            id={`filter-${filter.value}`}
                                                                            checked={displayConfig.filterCategories.includes(filter.value)}
                                                                            onChange={() => handleFilterCategoryChange(filter.value)}
                                                                        />
                                                                        <label className="custom-control-label" htmlFor={`filter-${filter.value}`}>
                                                                            {filter.label}
                                                                        </label>
                                                                    </div>
                                                                ))}
                                                                <small className="d-block text-muted">Filter chips this card appears under on the Benefits page (it always appears under "All benefits"). Leave all unticked to use the program's built-in default, if it has one — new programs with none ticked appear under "All benefits" only.</small>
                                                            </div>
                                                        </div>

                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-3 col-form-label">Audience Label</label>
                                                            <div className="col-12 col-md-9">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="audience"
                                                                    value={displayConfig.audience}
                                                                    onChange={handleDisplayConfigChange}
                                                                    placeholder="e.g., Whole household, Research & education"
                                                                    maxLength={30}
                                                                />
                                                                <small className="text-muted">Small label in the card footer</small>
                                                            </div>
                                                        </div>

                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-3 col-form-label">Frontend Path {!displayConfig.comingSoon && !displayConfig.hideCta && <span className="text-danger">*</span>}</label>
                                                            <div className="col-12 col-md-9">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="path"
                                                                    value={displayConfig.path}
                                                                    onChange={handleDisplayConfigChange}
                                                                    placeholder="e.g., /goaltide or https://partner.com/path"
                                                                />
                                                                <small className="text-muted">
                                                                    Where the "Access Now" button goes — an internal route (starts with /) or an external link (https://…, opens in a new tab)
                                                                    {displayConfig.comingSoon && '. Optional while Coming Soon — you can fill it in now for launch day.'}
                                                                    {!displayConfig.comingSoon && displayConfig.hideCta && '. Optional while the button is hidden.'}
                                                                </small>
                                                            </div>
                                                        </div>

                                                        {(displayConfig.comingSoon || notifyCounts?.waiting > 0 || notifyCounts?.notified > 0) && (
                                                            <div className="form-group row">
                                                                <label className="col-12 col-md-3 col-form-label">"Notify me" Sign-ups</label>
                                                                <div className="col-12 col-md-9">
                                                                    {displayConfig.comingSoon && (
                                                                        <div className="custom-control custom-switch mt-2">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="custom-control-input"
                                                                                id="notifyMe"
                                                                                name="notifyMe"
                                                                                checked={displayConfig.notifyMe}
                                                                                onChange={handleDisplayConfigChange}
                                                                            />
                                                                            <label className="custom-control-label" htmlFor="notifyMe">
                                                                                {displayConfig.notifyMe ? 'Card shows "Notify me" — visitors can leave their email' : 'No "Notify me" on this card'}
                                                                            </label>
                                                                        </div>
                                                                    )}
                                                                    {id && notifyCounts && (
                                                                        <div className="mt-2">
                                                                            <span className="badge badge-info mr-2">{notifyCounts.waiting} waiting</span>
                                                                            <span className="badge badge-secondary mr-2" title="Sign-ups are deleted 90 days after their launch email">{notifyCounts.notified} emailed (last 90 days)</span>
                                                                            {notifyCounts.undeliverable > 0 && (
                                                                                <span className="badge badge-warning mr-2" title="Given up after 3 failed sends">{notifyCounts.undeliverable} undeliverable</span>
                                                                            )}
                                                                            <span className="mr-1" />
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-primary"
                                                                                onClick={handleSendLaunchEmail}
                                                                                disabled={Boolean(sendProgress) || notifyCounts.waiting === 0 || !isLiveOnBenefits(savedFlag)}
                                                                            >
                                                                                {sendProgress ? `Sending… ${sendProgress.sent}/${sendProgress.total}` : 'Send launch email'}
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                    <small className="d-block text-muted mt-1">
                                                                        {id && notifyCounts && notifyCounts.waiting > 0 && !isLiveOnBenefits(savedFlag)
                                                                            ? 'To send, turn off Coming Soon, make sure the flag is enabled and active, and save — then come back to this screen.'
                                                                            : 'Everyone waiting gets one email saying this benefit is live. Nothing is sent until you click Send.'}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}

                                                {/* Configuration */}
                                                <h5 className="f_blue pb-3 mb-3 mt-4 border-bottom">Configuration (JSON)</h5>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Config</label>
                                                    <div className="col-12 col-md-9">
                                                        <textarea
                                                            className={`form-control ${configError ? 'is-invalid' : ''}`}
                                                            value={configJson}
                                                            onChange={handleConfigChange}
                                                            rows="5"
                                                            style={{ fontFamily: 'monospace' }}
                                                            placeholder='{"key": "value"}'
                                                        />
                                                        {configError && (
                                                            <div className="invalid-feedback">{configError}</div>
                                                        )}
                                                        <small className="text-muted">Feature-specific configuration in JSON format</small>
                                                    </div>
                                                </div>

                                                {/* Metadata */}
                                                <h5 className="f_blue pb-3 mb-3 mt-4 border-bottom">Metadata</h5>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Version</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="metadata.version"
                                                            value={formData.metadata.version}
                                                            onChange={handleInputChange}
                                                            placeholder="1.0"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Notes</label>
                                                    <div className="col-12 col-md-9">
                                                        <textarea
                                                            className="form-control"
                                                            name="metadata.notes"
                                                            value={formData.metadata.notes}
                                                            onChange={handleInputChange}
                                                            rows="3"
                                                            placeholder="Internal notes about this flag"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="card-footer text-center">
                                                <button
                                                    type="submit"
                                                    className="btn btn-blue"
                                                    disabled={saving}
                                                >
                                                    {saving ? 'Saving...' : (id ? 'Update' : 'Create')}
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ProgramFlagsAdd
